import React from "react";
import { formatMinutes, formatTime, formatWrittenDayMonthYear } from "shared/utils/momentutils";
import { formatDayOffset } from "shared/utils/purpleHeaderUtils";
import { capitalize } from "shared/utils/stringutils";
import { PurpleHeaderCard, PurpleHeaderDivider, PurpleHeaderVerticalDivider } from "./PurpleHeaderCard";
import { PurpleHeaderPeopleRow } from "./PurpleHeaderPeopleRow";
import { usePurpleHeaderLayout } from "./usePurpleHeaderLayout";

interface TravelPoint {
  name: string | undefined
  address: string | undefined
}

interface TravelHeaderLayoutProps {
  icon: JSX.Element
  departure: TravelPoint
  arrival: TravelPoint
  dateTime: server.dto.AppointmentDateTime
  detail: string | undefined
  durationInMinutes: number | undefined
  artist: server.dto.ArtistSmall | undefined
  passengers: string[]
}

export const TravelHeaderLayout = ({ icon, departure, arrival, dateTime, detail, durationInMinutes, artist, passengers }: TravelHeaderLayoutProps) => {
  const layout = usePurpleHeaderLayout();
  const isNight = dateTime.mode === "hasTimeStartsInNight";
  const arrivalDayOffset = formatDayOffset(dateTime.startDisplay, dateTime.endDisplay);
  const duration = formatMinutes(durationInMinutes);

  const endTime = duration && <>
    {formatTime(dateTime.endDisplay)}
    {arrivalDayOffset && <span className="purple-header-day-offset">{arrivalDayOffset}</span>}
  </>;

  const departureInfo = <div className="purple-header-travel-column">
    <div className="purple-header-title">{capitalize(departure.name)}</div>
    <div className="purple-header-text purple-header-light">{departure.address}</div>
  </div>;

  const arrivalInfo = <div className="purple-header-travel-column purple-header-travel-right">
    <div className="purple-header-title">{capitalize(arrival.name)}</div>
    <div className="purple-header-text purple-header-light">{arrival.address}</div>
  </div>;

  const middleTexts = <>
    <div className="purple-header-text purple-header-light">{detail}</div>
    <div className="purple-header-text purple-header-light">{duration}</div>
  </>;

  const peopleRow = <PurpleHeaderPeopleRow
    artistName={artist?.name}
    contactNames={passengers}
  />;

  if (layout === "short") {
    return <PurpleHeaderCard>
      <div className="purple-header-travel-grid">
        {departureInfo}
        <div className="purple-header-travel-middle purple-header-travel-middle-two-rows">
          {icon}
          {middleTexts}
        </div>
        {arrivalInfo}
        <div className="purple-header-travel-column">
          <div className="purple-header-subtitle">{formatTime(dateTime.startDisplay)}</div>
          {isNight && <div className="purple-header-text">{RESX.DateTime.InTheNightOf}</div>}
          <div className="purple-header-text">{formatWrittenDayMonthYear(dateTime.startDisplay)}</div>
        </div>
        <div className="purple-header-travel-column purple-header-travel-right">
          <div className="purple-header-subtitle">{endTime}</div>
        </div>
      </div>
      <PurpleHeaderDivider />
      {peopleRow}
    </PurpleHeaderCard>;
  }

  return <PurpleHeaderCard wide>
    <div className="purple-header-wide-main">
      <div className="purple-header-travel-grid">
        {departureInfo}
        <div className="purple-header-travel-middle">{middleTexts}</div>
        {arrivalInfo}
      </div>
      {peopleRow}
    </div>
    <PurpleHeaderVerticalDivider />
    <div className="purple-header-wide-date">
      {isNight && <div className="purple-header-text">{RESX.DateTime.InTheNightOf}</div>}
      <div className="purple-header-text">{formatWrittenDayMonthYear(dateTime.startDisplay)}</div>
      <div className="purple-header-subtitle">
        {formatTime(dateTime.startDisplay)}
        {duration && <> - {endTime}</>}
      </div>
    </div>
    <PurpleHeaderVerticalDivider />
    <div className="purple-header-wide-icons">{icon}</div>
  </PurpleHeaderCard>;
};
